import { ColDef, ColGroupDef } from 'ag-grid-community';
import { AgNumberFilterComponent } from '../components/ag-number-filter/ag-number-filter.component';
import { AgSetFilterComponent } from '../components/ag-set-filter/ag-set-filter.component';
import { AgTextFilterComponent } from '../components/ag-text-filter/ag-text-filter.component';
import { AgTextFloatingFilterComponent } from '../components/ag-text-filter/ag-text-floating-filter.component';
import { AgNumberFloatingFilterComponent } from '../components/ag-number-filter/ag-number-floating-filter.component';
import { isNumericColumn } from './empty-value.utils';

/**
 * Text, numeric and set columns get the app's own filter popups
 * (AgTextFilterComponent WAP-1646, AgNumberFilterComponent WAP-1647,
 * AgSetFilterComponent WAP-1648); the remaining provided ag-grid filter
 * (date) gets a
 * Reset button in its popup, so a user can drop a condition such as Blank
 * without editing the floating input (WAP-1643). `filterParams` is not deep-merged by ag-grid,
 * so this walks the definitions instead of relying on defaultColDef. A column
 * that already sets `buttons` keeps its choice; custom filter components
 * (class references) are left alone - they render their own footer.
 */
const RESET_BUTTONS: ReadonlyArray<'reset'> = ['reset'];

export function withFilterResetButton<T extends ColDef | ColGroupDef>(
  colDefs: T[],
): T[] {
  return colDefs.map((def) => {
    let result: T = def;
    if ('children' in def && Array.isArray(def.children)) {
      result = { ...def, children: withFilterResetButton(def.children) };
    } else if (usesTextFilter(def as ColDef)) {
      result = {
        ...def,
        filter: AgTextFilterComponent,
        ...floatingFilterFor(def as ColDef, AgTextFloatingFilterComponent),
      };
    } else if (usesNumberFilter(def as ColDef)) {
      result = {
        ...def,
        filter: AgNumberFilterComponent,
        ...floatingFilterFor(def as ColDef, AgNumberFloatingFilterComponent),
      };
    } else if ((def as ColDef).filter === 'agSetColumnFilter') {
      result = { ...def, filter: AgSetFilterComponent };
    } else if (usesProvidedFilter(def as ColDef)) {
      const filterParams = (def as ColDef).filterParams ?? {};
      if (!('buttons' in filterParams)) {
        result = {
          ...def,
          filterParams: { ...filterParams, buttons: [...RESET_BUTTONS] },
        };
      }
    }
    return result;
  });
}

/**
 * The typeable header-row box that pairs with an own filter popup; ag-grid
 * would otherwise render a read-only label for a custom filter. A column that
 * sets its own floatingFilterComponent, or turns the floating row off, wins.
 */
function floatingFilterFor(
  colDef: ColDef,
  component: unknown,
): Pick<ColDef, 'floatingFilterComponent'> {
  return colDef.floatingFilterComponent !== undefined ||
    colDef.floatingFilter === false
    ? {}
    : { floatingFilterComponent: component };
}

/**
 * Columns that would get ag-grid's text filter: the explicit name, or no
 * `filter` key on a column that is not numeric (defaultColDef sets `filter:
 * true`, which ag-grid resolves to the text filter for string data). They get
 * AgTextFilterComponent instead (WAP-1646). `filter: false` is respected.
 */
function usesTextFilter(colDef: ColDef): boolean {
  const filter = colDef.filter;
  return (
    filter === 'agTextColumnFilter' ||
    ((filter === undefined || filter === true) && !isNumericColumn(colDef))
  );
}

/**
 * Columns that would get ag-grid's number filter: the explicit name, or no
 * `filter` key on a numeric (right-aligned) column. They get
 * AgNumberFilterComponent instead (WAP-1647).
 */
function usesNumberFilter(colDef: ColDef): boolean {
  const filter = colDef.filter;
  return (
    filter === 'agNumberColumnFilter' ||
    ((filter === undefined || filter === true) && isNumericColumn(colDef))
  );
}

/** `true` or one of ag-grid's own filter names; a component class is a custom filter. */
function usesProvidedFilter(colDef: ColDef): boolean {
  const filter = colDef.filter;
  return filter === true || typeof filter === 'string';
}
